import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  createScheduleEventApi,
  fetchApiEvents,
  fetchApiMasterEvents,
  fetchScheduleBoard,
  fetchSubmittedPlaylistsByShows,
  saveScheduleBoard,
  updateScheduleEventApi,
} from "@apis/scheduleApis";
import { AxiosResponse } from "axios";
import { QUERY_KEYS } from "./queryKeys";
import { ApiResponse } from "@src/types/httpApi";
import {
  ICreateScheduleForm,
  IScheduleBoardEntry,
  IScheduleBoardState,
  IScheduleEvent,
  IMasterScheduleEvent,
  ISubmittedPlaylistLite,
} from "@src/types/schedules";

// Hook to fetch real events from the API
export const useApiEventsQuery = (
  startTime: Date,
  endTime: Date,
  options?: UseQueryOptions<AxiosResponse<ApiResponse<IScheduleEvent[]>>, Error>
): UseQueryResult<AxiosResponse<ApiResponse<IScheduleEvent[]>>, Error> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_SCHEDULE, startTime, endTime],
    queryFn: () => fetchApiEvents(startTime, endTime),
    placeholderData: { data: { data: [] } } as AxiosResponse<any>,
    ...options,
  });
};

// mutation = create schedule event
export const useCreateScheduleEventMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    { data: ICreateScheduleForm },
    unknown
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { data: ICreateScheduleForm },
  unknown
> => {
  return useMutation({
    mutationFn: ({ data }: { data: ICreateScheduleForm }) =>
      createScheduleEventApi(data),
    ...options,
  });
};

export const useApiMasterScheduleEventsQuery = (
  startTime: Date,
  endTime: Date,
  options?: UseQueryOptions<
    AxiosResponse<ApiResponse<IMasterScheduleEvent[]>>,
    Error
  >
): UseQueryResult<
  AxiosResponse<ApiResponse<IMasterScheduleEvent[]>>,
  Error
> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MASTER_SCHEDULE],
    queryFn: () => fetchApiMasterEvents(),
    placeholderData: { data: { data: [] } } as AxiosResponse<any>,
    ...options,
  });
};

export const useScheduleBoardQuery = (
  options?: Omit<
    UseQueryOptions<AxiosResponse<ApiResponse<IScheduleBoardEntry>>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<AxiosResponse<ApiResponse<IScheduleBoardEntry>>, Error> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_SCHEDULE_BOARD],
    queryFn: () => fetchScheduleBoard(),
    ...options,
  });
};

export const useSaveScheduleBoardMutation = (
  options?: UseMutationOptions<
    AxiosResponse<ApiResponse<IScheduleBoardEntry>>,
    Error,
    { state: IScheduleBoardState },
    unknown
  >
): UseMutationResult<
  AxiosResponse<ApiResponse<IScheduleBoardEntry>>,
  Error,
  { state: IScheduleBoardState },
  unknown
> => {
  return useMutation({
    mutationFn: ({ state }: { state: IScheduleBoardState }) =>
      saveScheduleBoard(state),
    ...options,
  });
};

// Hook to fetch the "submitted" playlists for the given shows, so red calendar
// boxes (slots with no attached playlist) can show how many were submitted.
export const useSubmittedPlaylistsByShowQuery = (
  showDocumentIds: string[],
  options?: UseQueryOptions<
    AxiosResponse<ApiResponse<ISubmittedPlaylistLite[]>>,
    Error
  >
): UseQueryResult<
  AxiosResponse<ApiResponse<ISubmittedPlaylistLite[]>>,
  Error
> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_SUBMITTED_PLAYLISTS_BY_SHOW, showDocumentIds],
    queryFn: () => fetchSubmittedPlaylistsByShows(showDocumentIds),
    enabled: showDocumentIds.length > 0,
    // No placeholderData on purpose: an empty placeholder resolves the query to
    // "success" with zero rows, so every red slot would render "No playlists
    // submitted" before the real counts arrive. Consumers read isLoading and
    // render a neutral placeholder instead of a false zero.
    ...options,
  });
};

export const useUpdateScheduleEventMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    { data: ICreateScheduleForm & { scheduleId: string } },
    unknown
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { data: ICreateScheduleForm & { scheduleId: string } },
  unknown
> => {
  return useMutation({
    mutationFn: ({
      data,
    }: {
      data: ICreateScheduleForm & { scheduleId: string };
    }) => updateScheduleEventApi(data),
    ...options,
  });
};
